import React, { useCallback, useMemo, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { token } from '@atlaskit/tokens';
import { HierarchyChart, type HierarchyData } from '@atlassian/reporting-lines/hierarchy-chart';
import { InteractiveHierarchyModal } from '@atlassian/reporting-lines/interactive-hierarchy-modal';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import type { Collaborator, PersonProfileData, RecentWorkUrls } from '../../types';
import { appendWorkSubpath } from '../utils/profileWorkUrl';

import { ActionBar } from './ActionBar';
import { Collaborators } from './Collaborators';
import { ProfileIdentity } from './ProfileIdentity';

const messages = defineMessages({
	tabRecentWork: {
		id: 'user-profile-preview.full.tab-recent-work',
		defaultMessage: 'Recent work',
		description: 'Tab label for the recent work section in the user profile preview',
	},
	tabReportingLine: {
		id: 'user-profile-preview.full.tab-reporting-line',
		defaultMessage: 'Reporting line',
		description: 'Tab label for the reporting line section in the user profile preview',
	},
	tabCollaborators: {
		id: 'user-profile-preview.full.tab-collaborators',
		defaultMessage: 'Collaborators',
		description: 'Tab label for collaborators on the user profile preview',
	},
});

const styles = cssMap({
	tabPanelSurface: {
		marginTop: token('space.150'),
		width: '100%',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.large'),
	},
	tabPanelInsetLoose: {
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	tabPanelInsetTight: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
});

interface FullVariantProps {
	profileData: PersonProfileData;
	hierarchyData?: HierarchyData;
	recentWorkData?: RecentWorkUrls;
	collaborators?: Collaborator[];
	testId?: string;
	cloudId: string;
	orgId?: string;
	siteId?: string;
	requestingUserId?: string;
	onLinkClick?: (
		url: string,
		index: number,
		opts?: { isAuxClick?: boolean; isContextMenu?: boolean },
	) => void;
}

export function FullVariant({
	profileData,
	hierarchyData,
	recentWorkData,
	collaborators,
	testId,
	cloudId,
	orgId,
	siteId,
	requestingUserId,
	onLinkClick,
}: FullVariantProps): React.JSX.Element {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const [interactiveModalAccountId, setInteractiveModalAccountId] = useState<string | null>(null);
	const handleNodeClick = useCallback((accountId: string) => {
		setInteractiveModalAccountId(accountId);
	}, []);
	const handleModalClose = useCallback(() => {
		setInteractiveModalAccountId(null);
	}, []);
	const hasRecentWork = !!(recentWorkData && recentWorkData.length > 0);
	const hasHierarchy = !!hierarchyData && hierarchyData.user?.name;
	const hasCollaborators = !!collaborators && collaborators.length > 0;
	const showTabs = hasRecentWork || hasHierarchy || hasCollaborators;

	// Tabs are conditionally rendered, so map the visible tab index to a stable tab name.
	const tabNames = useMemo(() => {
		const names: ('recentWork' | 'reportingLine' | 'collaborators')[] = [];
		if (hasRecentWork) {
			names.push('recentWork');
		}
		if (hasHierarchy) {
			names.push('reportingLine');
		}
		if (hasCollaborators) {
			names.push('collaborators');
		}
		return names;
	}, [hasRecentWork, hasHierarchy, hasCollaborators]);

	const handleTabChange = useCallback(
		(index: number) => {
			const tab = tabNames[index];
			if (tab) {
				fireEvent('ui.userProfilePreview.clicked.tab', { tab });
			}
		},
		[fireEvent, tabNames],
	);

	const handleViewAllRecentWorkClick = useCallback(
		() => fireEvent('ui.userProfilePreview.clicked.recentWorkViewAll'),
		[fireEvent],
	);

	return (
		<>
			<Stack space="space.150" testId={testId}>
				<Inline space="space.200" alignBlock="center">
					<ProfileIdentity
						profileData={profileData}
						avatarSize="xlarge"
						showDepartment
						showLocation
						actions={<ActionBar profileData={profileData} showOrgChart={false} cloudId={cloudId} />}
					/>
				</Inline>

				{showTabs && (
					<Tabs id="person-profile-card-tabs" onChange={handleTabChange}>
						<TabList>
							{hasRecentWork && <Tab>{intl.formatMessage(messages.tabRecentWork)}</Tab>}
							{hasHierarchy && <Tab>{intl.formatMessage(messages.tabReportingLine)}</Tab>}
							{hasCollaborators && <Tab>{intl.formatMessage(messages.tabCollaborators)}</Tab>}
						</TabList>

						{hasRecentWork && (
							<TabPanel>
								<Box xcss={styles.tabPanelSurface}>
									<Box testId={testId ? `${testId}-recent-work` : undefined}>
										<LinkList
											urls={recentWorkData}
											onLinkClick={onLinkClick}
											maxSources={3}
											expandedMaxSources={7}
											viewAllUrl={appendWorkSubpath(
												navigateToTeamsApp({
													type: 'USER',
													payload: { userId: profileData.accountId },
													cloudId,
												}).href,
											)}
											onViewAllLinkClick={handleViewAllRecentWorkClick}
										/>
									</Box>
								</Box>
							</TabPanel>
						)}
						{hasHierarchy && (
							<TabPanel>
								<Box xcss={cx(styles.tabPanelSurface, styles.tabPanelInsetLoose)}>
									<HierarchyChart
										data={hierarchyData}
										cloudId={cloudId}
										accountId={profileData.accountId}
										orgId={orgId}
										onNodeClick={handleNodeClick}
										isPreview
										onViewFullReportingLine={() =>
											setInteractiveModalAccountId(profileData.accountId)
										}
										requestingUserId={requestingUserId}
									/>
								</Box>
							</TabPanel>
						)}
						{hasCollaborators && collaborators && (
							<TabPanel>
								<Box xcss={cx(styles.tabPanelSurface, styles.tabPanelInsetTight)}>
									<Collaborators collaborators={collaborators} cloudId={cloudId} orgId={orgId} />
								</Box>
							</TabPanel>
						)}
					</Tabs>
				)}
			</Stack>
			<ModalTransition>
				{interactiveModalAccountId && siteId && (
					<InteractiveHierarchyModal
						accountId={interactiveModalAccountId}
						orgId={orgId}
						siteId={siteId}
						cloudId={cloudId}
						onClose={handleModalClose}
						requestingUserId={requestingUserId}
					/>
				)}
			</ModalTransition>
		</>
	);
}
